import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { usePageTransition } from './TransitionProvider';
import logoFinaal from '../images/logo-finaal.png';
import '../styles/navbar.css';

let SplitText;

const Navbar = () => {
  const location = useLocation();
  const { start } = usePageTransition();
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);
  const logoSvgRef = useRef(null);
  const menuHeaderRef = useRef(null);
  const menuOverlayRef = useRef(null);
  const [time, setTime] = React.useState('00:00:00');
  const isOpenRef = useRef(false);
  const lastScrollYRef = useRef(window.scrollY);
  const isMenuVisibleRef = useRef(true);
  const isAnimatingRef = useRef(false);
  const splitTextsRef = useRef([]);
  const footerSplitTextsRef = useRef([]);

  useEffect(() => {
    // Initialize menu position
    const menu = menuRef.current;
    if (menu) {
      gsap.set(menu, { y: 0 });
    }

    // Load SplitText
    const loadSplitText = async () => {
      try {
        // Check if SplitText is already loaded
        if (window.SplitText) {
          SplitText = window.SplitText;
          gsap.registerPlugin(SplitText);
        } else {
          // Load SplitText via script tag
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/SplitText.min.js';
          script.async = true;

          script.onload = () => {
            SplitText = window.SplitText;
            if (SplitText) {
              gsap.registerPlugin(SplitText);
            }
          };

          script.onerror = () => {
            console.warn('SplitText not available');
          };

          document.head.appendChild(script);

          // Wait for script to load
          await new Promise((resolve) => {
            script.onload = () => {
              SplitText = window.SplitText;
              if (SplitText) {
                gsap.registerPlugin(SplitText);
              }
              resolve();
            };
            script.onerror = () => {
              console.warn('SplitText not available');
              resolve();
            };
          });
        }
      } catch (error) {
        console.warn('SplitText not available:', error);
      } finally {
        // Wait for fonts to load
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => {
            setTimeout(() => {
              initNavbar();
            }, 100);
          });
        } else {
          setTimeout(() => {
            initNavbar();
          }, 500);
        }
      }
    };

    loadSplitText();
    setupScrollHandler();
    updateTime();
    const timeInterval = setInterval(updateTime, 1000);

    return () => {
      clearInterval(timeInterval);
      window.removeEventListener('scroll', handleScroll);
      // Cleanup GSAP tweens
      if (menu) {
        gsap.killTweensOf(menu);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateTime = () => {
    const now = new Date();
    const timeString = now.toLocaleTimeString('en-US', {
      hour12: false,
    });
    setTime(`${timeString} LOCAL`);
  };

  const setupScrollHandler = () => {
    window.addEventListener('scroll', handleScroll, { passive: true });
  };

  const handleScroll = () => {
    const currentScrollY = window.scrollY;
    const menu = menuRef.current;

    if (!menu) return;

    // Als menu open is en we scrollen naar beneden, sluit menu
    if (currentScrollY > lastScrollYRef.current && currentScrollY > 100) {
      if (isOpenRef.current) {
        closeMenu();
      }
      // Animate menu naar boven (verbergen) - alleen als menu niet al verborgen is
      if (isMenuVisibleRef.current) {
        gsap.killTweensOf(menu);
        gsap.to(menu, {
          duration: 0.4,
          y: -200,
          ease: 'power2.out',
          overwrite: true,
        });
        isMenuVisibleRef.current = false;
      }
    } else if (currentScrollY < lastScrollYRef.current) {
      // Animate menu terug (tonen) - alleen als menu verborgen is
      if (!isMenuVisibleRef.current) {
        gsap.killTweensOf(menu);
        gsap.to(menu, {
          duration: 0.4,
          y: 0,
          ease: 'power2.out',
          overwrite: true,
        });
        isMenuVisibleRef.current = true;
      }
    }

    lastScrollYRef.current = currentScrollY;
  };

  const scrambleText = (elements, duration = 0.4) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()';

    elements.forEach((char) => {
      const originalText = char.textContent;
      let iterations = 0;
      const maxIterations = Math.floor(Math.random() * 6) + 3;

      gsap.set(char, { opacity: 1 });

      const scrambleInterval = setInterval(() => {
        char.textContent = chars[Math.floor(Math.random() * chars.length)];
        iterations++;

        if (iterations >= maxIterations) {
          clearInterval(scrambleInterval);
          char.textContent = originalText;
        }
      }, 25);

      setTimeout(() => {
        clearInterval(scrambleInterval);
        char.textContent = originalText;
      }, duration * 1000);
    });
  };

  const initNavbar = () => {
    const menuOverlay = menuOverlayRef.current;
    const menuItems = document.querySelectorAll('.menu-nav li');

    if (!menuOverlay || !menuItems.length) {
      console.error('Menu elements not found!');
      return;
    }

    gsap.set(menuOverlay, {
      scaleY: 0,
      transformOrigin: 'top center',
    });

    menuItems.forEach((item) => {
      const link = item.querySelector('a');
      if (link) {
        try {
          if (SplitText) {
            const split = new SplitText(link, {
              type: 'words',
              mask: 'words',
            });
            splitTextsRef.current.push(split);

            gsap.set(split.words, {
              yPercent: 120,
            });
          } else {
            gsap.set(link, { opacity: 1 });
          }
        } catch (error) {
          console.error('SplitText error:', error);
          gsap.set(link, { opacity: 1 });
        }
      }
    });

    const footerElements = document.querySelectorAll(
      '.menu-social a, .menu-social span, .menu-time'
    );
    footerElements.forEach((element) => {
      try {
        if (SplitText) {
          const split = new SplitText(element, {
            type: 'chars',
          });
          footerSplitTextsRef.current.push(split);

          gsap.set(split.chars, {
            opacity: 0,
          });
        }
      } catch (error) {
        console.error('SplitText footer error:', error);
      }
    });

    gsap.set(menuItems, {
      opacity: 1,
    });

    const menuFooter = document.querySelector('.menu-footer');
    if (menuFooter) {
      gsap.set(menuFooter, {
        opacity: 1,
        y: 20,
      });
    }
  };

  const toggleMenu = () => {
    if (isAnimatingRef.current) return;

    if (isOpenRef.current) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  const openMenu = () => {
    isOpenRef.current = true;
    isAnimatingRef.current = true;
    const hamburger = hamburgerRef.current;
    const logo = logoSvgRef.current;

    if (hamburger) hamburger.classList.add('open');
    if (logo) logo.classList.add('rotated');

    const menuOverlay = menuOverlayRef.current;
    const menuItems = document.querySelectorAll('.menu-nav li');
    const menuFooter = document.querySelector('.menu-footer');

    const tl = gsap.timeline({
      onComplete: () => {
        isAnimatingRef.current = false;
      },
    });

    tl.to(menuOverlay, {
      duration: 0.5,
      scaleY: 1,
      ease: 'power3.out',
    });

    const allWords = splitTextsRef.current.reduce((acc, split) => {
      if (split.words) {
        return acc.concat(split.words);
      }
      return acc;
    }, []);

    if (allWords.length > 0) {
      tl.to(
        allWords,
        {
          duration: 0.75,
          yPercent: 0,
          stagger: 0.05,
          ease: 'power4.out',
        },
        '-=0.3'
      );
    } else {
      tl.to(
        menuItems,
        {
          duration: 0.5,
          opacity: 1,
          stagger: 0.1,
          ease: 'power2.out',
        },
        '-=0.3'
      );
    }

    tl.to(
      menuFooter,
      {
        duration: 0.3,
        y: 0,
        ease: 'power2.out',
        onComplete: () => {
          const timeElement = document.querySelector('.menu-time');
          if (timeElement) {
            gsap.set(timeElement, { opacity: 1 });
          }

          const allFooterChars = footerSplitTextsRef.current.reduce((acc, split) => {
            if (split.chars) {
              return acc.concat(split.chars);
            }
            return acc;
          }, []);

          if (allFooterChars.length > 0) {
            allFooterChars.forEach((char, index) => {
              setTimeout(() => {
                scrambleText([char], 0.4);
              }, index * 30);
            });
          }
        },
      },
      '-=1'
    );
  };

  const closeMenu = () => {
    isOpenRef.current = false;
    isAnimatingRef.current = true;
    const hamburger = hamburgerRef.current;
    const logo = logoSvgRef.current;

    if (hamburger) hamburger.classList.remove('open');
    if (logo) logo.classList.remove('rotated');

    const menuOverlay = menuOverlayRef.current;
    const menuItems = document.querySelectorAll('.menu-nav li');
    const menuFooter = document.querySelector('.menu-footer');

    const tl = gsap.timeline({
      onComplete: () => {
        isAnimatingRef.current = false;
      },
    });

    const allWords = splitTextsRef.current.reduce((acc, split) => {
      if (split.words) {
        return acc.concat(split.words);
      }
      return acc;
    }, []);

    tl.to(
      [menuFooter],
      {
        duration: 0.3,
        y: 20,
        ease: 'power2.in',
        onStart: () => {
          const timeElement = document.querySelector('.menu-time');
          if (timeElement) {
            gsap.set(timeElement, { opacity: 0 });
          }
          const allFooterChars = footerSplitTextsRef.current.reduce((acc, split) => {
            if (split.chars) {
              return acc.concat(split.chars);
            }
            return acc;
          }, []);
          gsap.set(allFooterChars, { opacity: 0 });
        },
      }
    );

    if (allWords.length > 0) {
      tl.to(
        allWords,
        {
          duration: 0.25,
          yPercent: 120,
          stagger: -0.025,
          ease: 'power2.in',
        },
        '-=0.25'
      );
    } else {
      tl.to(
        menuItems,
        {
          duration: 0.25,
          opacity: 0,
          stagger: -0.025,
          ease: 'power2.in',
        },
        '-=0.25'
      );
    }

    tl.to(
      menuOverlay,
      {
        duration: 0.5,
        scaleY: 0,
        ease: 'power3.inOut',
      },
      '-=0.2'
    );
  };

  const handleLinkClick = (path, e) => {
    e.preventDefault();

    // Only navigate if not already on the target page
    if (location.pathname !== path) {
      if (isOpenRef.current) {
        // Close menu first, then navigate after closing animation
        closeMenu();
        setTimeout(() => {
          start(path);
        }, 600); // Wait for menu close animation
      } else {
        // Navigate immediately if menu is not open
        start(path);
      }
    } else if (isOpenRef.current) {
      // If already on page, just close the menu
      closeMenu();
    }
  };

  return (
    <nav className="menu" ref={menuRef} id="navbar">
      <div className="menu-header" ref={menuHeaderRef} onClick={toggleMenu}>
        <a href="/" className="menu-logo" onClick={(e) => handleLinkClick('/', e)}>
          <span ref={logoSvgRef} className="menu-logo-text">
            <img src={logoFinaal} alt="SK — Sander Kuyken" className="menu-logo-img" />
          </span>
        </a>
        <button className="menu-toggle" aria-label="Toggle menu" onClick={(e) => {
          e.stopPropagation();
          toggleMenu();
        }}>
          <div className="menu-hamburger-icon" ref={hamburgerRef}>
            <span className="menu-item"></span>
            <span className="menu-item"></span>
          </div>
        </button>
      </div>
      <div className="menu-overlay" ref={menuOverlayRef}>
        <nav className="menu-nav">
          <ul>
            <li>
              <a href="/" onClick={(e) => handleLinkClick('/', e)}>
                Home
              </a>
            </li>
            <li>
              <a href="/about" onClick={(e) => handleLinkClick('/about', e)}>
                About
              </a>
            </li>
            <li>
              <a href="/works" onClick={(e) => handleLinkClick('/works', e)}>
                Works
              </a>
            </li>
            <li>
              <a href="/services" onClick={(e) => handleLinkClick('/services', e)}>
                Services
              </a>
            </li>
            <li>
              <a href="/contact" onClick={(e) => handleLinkClick('/contact', e)}>
                Contact
              </a>
            </li>
          </ul>
        </nav>
        <div className="menu-footer">
          <div className="menu-social">
            <a href="https://www.artstation.com/kuyken" target="_blank" rel="noopener noreferrer">
              <span>&#9654;</span> ArtStation
            </a>
            <a
              href="https://www.linkedin.com/in/sander-kuyken-8a384716b/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>&#9654;</span> LinkedIn
            </a>
          </div>
          <div className="menu-time">{time}</div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;

